import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  DateRange,
  LicenseInfo,
  LocalizationProvider,
  PickersShortcutsItem,
  SingleInputDateRangeField
} from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { calculateRange } from 'src/@core/utils/getDateRange';
import {
  endOfDay,
  endOfMonth,
  endOfToday,
  endOfTomorrow,
  endOfWeek,
  endOfYesterday,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfTomorrow,
  startOfWeek,
  startOfYesterday
} from 'date-fns';
import { isNull } from 'lodash';
import { useTheme } from '@mui/material';
import { getOverrideForToday } from './helpers';

// license the MUI controls
LicenseInfo.setLicenseKey(
  '9c0ebfdf99bcad1fabe46ea4a7dc3d67Tz0xMTc0MjgsRT0xNzg2MjMzNTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y'
);

export enum DateRangeShortcut {
  Today = 'Today',
  Yesterday = 'Yesterday',
  ThisWeek = 'This Week',
  LastWeek = 'Last Week',
  ThisMonth = 'This Month',
  Last3Months = 'Last 3 Months',
  Tomorrow = 'Tomorrow',
  NextWeek = 'Next Week'
}

export interface IVigoDateRangeReturn {
  dateFrom: Date | null;
  dateTo: Date | null;
  selectedRange: string;
}

export interface IVigoDateRange {
  name: string;
  variant?: 'contained' | 'outlined' | 'text';
  onChange?: (data: IVigoDateRangeReturn) => any;
  showPast?: boolean;
  showFuture?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  maxDateRange?: number;
  allowCustom?: boolean;
  selectedRange?: string;
  maxPastDays?: number;
  maxFutureDays?: number;
  preventInputDateEditing?: boolean;
  disabledShortcuts?: DateRangeShortcut[];
}

const shortcutsItemsPast: PickersShortcutsItem<DateRange<any>>[] = [
  {
    label: DateRangeShortcut.Today,
    getValue: () => {
      return [startOfToday(), endOfToday()];
    }
  },
  {
    label: DateRangeShortcut.Yesterday,
    getValue: () => {
      return [startOfYesterday(), endOfYesterday()];
    }
  },
  {
    label: DateRangeShortcut.ThisWeek,
    getValue: () => {
      return [startOfWeek(new Date(), { weekStartsOn: 1 }), endOfWeek(new Date(), { weekStartsOn: 1 })];
    }
  },
  {
    label: DateRangeShortcut.LastWeek,
    getValue: () => {
      return [
        startOfWeek(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), { weekStartsOn: 1 }),
        endOfWeek(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), { weekStartsOn: 1 })
      ];
    }
  },
  {
    label: DateRangeShortcut.ThisMonth,
    getValue: () => {
      return [startOfMonth(new Date()), endOfMonth(new Date())];
    }
  },
  {
    label: DateRangeShortcut.Last3Months,
    getValue: () => {
      const today = new Date();
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      const startDate = startOfMonth(threeMonthsAgo);
      const endDate = endOfToday();

      return [startDate, endDate];
    }
  }
];

const shortcutsItemsFuture: PickersShortcutsItem<DateRange<any>>[] = [
  {
    label: DateRangeShortcut.Tomorrow,
    getValue: () => {
      return [startOfTomorrow(), endOfTomorrow()];
    }
  },
  {
    label: DateRangeShortcut.NextWeek,
    getValue: () => {
      return [
        startOfWeek(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), { weekStartsOn: 1 }),
        endOfWeek(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), { weekStartsOn: 1 })
      ];
    }
  }
];

const VigoDateRange = ({
  onChange,
  showPast,
  showFuture,
  disablePast,
  disableFuture,
  selectedRange,
  name,
  maxPastDays,
  maxFutureDays,
  preventInputDateEditing,
  disabledShortcuts = []
}: IVigoDateRange) => {
  const [rangeOverride, setRangeOverride] = useState<boolean>(false);
  const [range, setRange] = useState<[Date, Date]>([new Date(), new Date()]);
  const [rangeOption, setRangeOption] = useState<string>(selectedRange ?? 'Custom');

  const inputRef = useRef<HTMLInputElement>(null);

  const theme = useTheme();

  const shortcutsItems: PickersShortcutsItem<DateRange<any>>[] = [];

  if (showPast) {
    shortcutsItems.push(
      ...shortcutsItemsPast.filter(item => !disabledShortcuts?.includes(item.label as DateRangeShortcut))
    );
  }

  if (showFuture) {
    shortcutsItems.push(
      ...shortcutsItemsFuture.filter(item => !disabledShortcuts?.includes(item.label as DateRangeShortcut))
    );
  }

  const minDate = useMemo(() => {
    if (rangeOverride || maxPastDays === undefined) return undefined;

    const date = new Date();
    date.setDate(date.getDate() - maxPastDays);

    return startOfDay(date);
  }, [maxPastDays, rangeOverride]);

  const maxDate = useMemo(() => {
    if (rangeOverride || maxFutureDays === undefined) return undefined;

    const date = new Date();
    date.setDate(date.getDate() + maxFutureDays);

    return endOfDay(date);
  }, [maxFutureDays, rangeOverride]);

  const handleAccept = (value: DateRange<any>) => {
    let rangeObject: IVigoDateRangeReturn;
    const validatedRange = [...value] as DateRange<any>;

    if (isNull(value[0]) || isNull(value[1])) {
      rangeObject = calculateRange('Today', '');
      setRangeOption('Today');
    } else {
      // Validate dates against min and max constraints
      if (minDate && value[0] < minDate) {
        validatedRange[0] = minDate;
      }
      if (minDate && value[1] < minDate) {
        validatedRange[1] = minDate;
      }
      if (maxDate && value[0] > maxDate) {
        validatedRange[0] = maxDate;
      }
      if (maxDate && value[1] > maxDate) {
        validatedRange[1] = maxDate;
      }

      // If dates were adjusted, update the component state
      if (value[0] !== validatedRange[0] || value[1] !== validatedRange[1]) {
        setRange([validatedRange[0], validatedRange[1]] as [Date, Date]);
      }

      rangeObject = calculateRange(rangeOption, validatedRange);
    }

    if (onChange) {
      onChange(rangeObject);
    }
  };

  const handleChange = (newValue: DateRange<Date>) => {
    // Skip validation if either date is null
    if (isNull(newValue[0]) || isNull(newValue[1])) {
      setRange(newValue as [Date, Date]);

      return;
    }

    // Create a copy to validate
    const validatedRange: [Date, Date] = [...newValue] as [Date, Date];
    let needsAdjustment = false;

    // Apply the same validation logic as in handleAccept
    if (minDate && validatedRange[0] < minDate) {
      validatedRange[0] = minDate;
      needsAdjustment = true;
    }
    if (minDate && validatedRange[1] < minDate) {
      validatedRange[1] = minDate;
      needsAdjustment = true;
    }
    if (maxDate && validatedRange[0] > maxDate) {
      validatedRange[0] = maxDate;
      needsAdjustment = true;
    }
    if (maxDate && validatedRange[1] > maxDate) {
      validatedRange[1] = maxDate;
      needsAdjustment = true;
    }

    // Update with either the original or adjusted range
    setRange(needsAdjustment ? validatedRange : (newValue as [Date, Date]));

    // Set to custom since user manually changed the dates
    setRangeOption('Custom');
  };

  useEffect(() => {
    if (selectedRange) {
      const { dateFrom, dateTo }: IVigoDateRangeReturn = calculateRange(selectedRange.replaceAll('"', ''), 0);
      setRange([startOfDay(dateFrom as Date), endOfDay(dateTo as Date)] as [Date, Date]);
      setRangeOption('Custom');
    }
  }, [selectedRange]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();

      return false;
    };

    const currentInput = inputRef.current;
    if (currentInput && preventInputDateEditing) {
      currentInput.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (currentInput) {
        currentInput.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [inputRef, preventInputDateEditing]);

  useEffect(() => {
    const value = getOverrideForToday(name);
    if (value) {
      setRangeOverride(true);
    } else {
      setRangeOverride(false);
    }
  }, [name]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateRangePicker
        format="dd/MM/yy"
        disablePast={disablePast}
        disableFuture={disableFuture}
        minDate={minDate}
        maxDate={maxDate}
        onAccept={handleAccept}
        onChange={handleChange}
        closeOnSelect={false}
        onRangePositionChange={() => {
          setRangeOption('Custom');
        }}
        className={theme.palette.mode === 'dark' ? 'darkmodeDatarange' : ''}
        value={range}
        slots={{
          field: SingleInputDateRangeField
        }}
        slotProps={{
          textField: {
            inputRef: inputRef,
            sx: { textAlign: 'center', padding: '10px', width: '190px', '&:hover': { cursor: 'pointer' } },
            variant: 'outlined',
            size: 'small',
            name: name ?? ''
          },
          shortcuts: {
            items: shortcutsItems,
            onClick: (e: any) => {
              setRangeOption(e?.target?.textContent ?? '');
              const rangeObject: IVigoDateRangeReturn = calculateRange(e?.target?.textContent ?? '', range);

              if (onChange) {
                onChange(rangeObject);
              }
            }
          },
          actionBar: {
            className: theme.palette.mode === 'dark' ? 'darkmodeDatarange' : '',
            actions: ['clear', 'accept']
          }
        }}
      />
    </LocalizationProvider>
  );
};

export default VigoDateRange;
